import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["dialog", "trigger"]
  declare readonly dialogTarget: HTMLElement
  declare readonly triggerTarget: HTMLButtonElement

  private blurEnabled = true

  closeDialog(): void {
    this.dialogTarget.hidden = true
    this.triggerTarget.removeAttribute("aria-expanded")
    this.dispatch("dialogClosed")
  }

  closeDialogIfBlurred({ currentTarget, relatedTarget }: FocusEvent): void {
    if (!this.blurEnabled) {
      return
    }

    const containsFocus = (currentTarget as HTMLElement).contains(
      (relatedTarget as HTMLElement) || document.activeElement
    )

    if (!containsFocus) {
      this.closeDialog()
    }
  }

  disableBlur = (): void => {
    this.blurEnabled = false
  }

  enableBlur = (): void => {
    this.blurEnabled = true
  }

  openDialog(): void {
    this.dialogTarget.hidden = false
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this.setDialogPosition()

    // In most browsers when dialog trigger button is clicked it receives focus but in Safari it
    // does not: https://bugs.webkit.org/show_bug.cgi?id=22261. This means `closeDialogIfBlurred`
    // never runs because there's no blur. Manually focus trigger to mitigate.
    this.triggerTarget.focus({ preventScroll: true })
  }

  toggleDialog(): void {
    if (this.dialogTarget.hidden) {
      this.openDialog()
    } else {
      this.closeDialog()
    }
  }

  private setDialogPosition(): void {
    const relativeContainer = this.dialogTarget.offsetParent

    if (!relativeContainer) {
      return
    }

    const { left: containerLeft } = relativeContainer.getBoundingClientRect()
    const { left: triggerLeft, right: triggerRight } = this.triggerTarget.getBoundingClientRect()
    const { width: dialogWidth } = this.dialogTarget.getBoundingClientRect()
    const canFitLeft = triggerLeft + dialogWidth < document.documentElement.clientWidth
    const canFitRight = triggerRight - dialogWidth > 0
    let dialogOffsetLeft = 0

    if (canFitLeft) {
      dialogOffsetLeft = triggerLeft - containerLeft
    } else if (canFitRight) {
      dialogOffsetLeft = triggerRight - dialogWidth - containerLeft
    }

    this.dialogTarget.style.setProperty("--dialog-offset-left", `${dialogOffsetLeft}px`)
  }
}
